<template>
  <section id="realisations" class="py-20 bg-black">
    <div class="container mx-auto px-4">
      <!-- En-tête de section -->
      <div class="text-center mb-16">
        <h2 class="text-4xl font-bold text-white mb-4">
          Mes <span class="text-gradient">Réalisations</span>
        </h2>
        <p class="text-gray-300 max-w-2xl mx-auto">
          Découvrez mes projets les plus récents. Chaque projet représente une
          opportunité d'apprentissage et un défi relevé avec passion.
        </p>
      </div>

      <!-- Filtres de projets -->
      <div class="mb-12 flex flex-wrap justify-center gap-4">
        <button
          v-for="categorie in categories"
          :key="categorie"
          @click="filtrerPar(categorie)"
          :class="[
            'px-5 py-2 rounded-full transition-all duration-300 filter-btn',
            categorieActive === categorie
              ? 'active-filter'
              : 'inactive-filter',
          ]"
        >
          {{ categorie }}
        </button>
      </div>

      <!-- Grille de projets -->
      <transition-group
        name="projet-transition"
        tag="div"
        class="masonry-grid"
      >
        <div
          v-for="projet in projetsFiltres"
          :key="projet.id"
          class="masonry-item mb-8"
        >
          <div class="projet-card overflow-hidden shadow-xl">
            <!-- Bannière de catégorie -->
            <div class="category-ribbon">{{ projet.categorie }}</div>
            
            <!-- Image du projet avec overlay -->
            <div class="projet-image-container">
              <img
                :src="projet.image"
                :alt="projet.titre"
                class="projet-img"
              />
              <div class="projet-overlay">
                <div class="overlay-actions">
                  <a
                    v-if="projet.demo"
                    :href="projet.demo"
                    target="_blank"
                    class="action-btn demo-btn"
                  >
                    <i class="fas fa-eye mr-2"></i>Voir le projet
                  </a>
                  <a
                    v-if="projet.code && projet.id !== 7"
                    :href="projet.code"
                    target="_blank"
                    class="action-btn code-btn"
                  >
                    <i class="fab fa-github mr-2"></i>Voir le code
                  </a>
                  <a
                    v-if="projet.id === 7"
                    @click="openGallery(projet)"
                    class="action-btn code-btn cursor-pointer"
                  >
                    <i class="fas fa-images mr-2"></i>Voir la galerie
                  </a>
                </div>
              </div>
            </div>

            <!-- Contenu du projet -->
            <div class="projet-content">
              <h3 class="projet-title">{{ projet.titre }}</h3>
              
              <div class="mb-4">
                <p 
                  class="projet-description" 
                  :class="{ 'line-clamp-3': !projet.showFullDescription }"
                >
                  {{ projet.description }}
                </p>
                <button 
                  v-if="projet.description.length > 180" 
                  @click="toggleDescription(projet)"
                  class="read-more-btn"
                >
                  {{ projet.showFullDescription ? 'Voir moins' : 'Voir plus' }}
                  <i 
                    class="fas ml-1 text-xs" 
                    :class="[projet.showFullDescription ? 'fa-chevron-up' : 'fa-chevron-down']"
                  ></i>
                </button>
              </div>

              <!-- Technologies utilisées -->
              <div class="tech-badges">
                <span
                  v-for="(tech, index) in projet.technologies"
                  :key="index"
                  class="tech-badge"
                >
                  {{ tech }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <!-- Gallery Modal -->
    <transition name="modal-fade">
      <div v-if="showGallery" class="fixed inset-0 z-50" @click="closeGallery">
        <!-- Background overlay with dynamic blur effect -->
        <div 
          class="absolute inset-0 bg-black/95 backdrop-blur-md transition-all duration-500 flex items-center justify-center"
        >
          <!-- Indicateur visuel pour fermer -->
          <div class="text-white/30 text-sm font-medium border border-white/10 px-4 py-2 rounded-full backdrop-blur-md pointer-events-none">
            Cliquez à l'extérieur pour fermer
          </div>
        </div>

        <!-- Main modal container -->
        <div class="relative h-full flex flex-col" @click.stop>
          <!-- Header with glassmorphism -->
          <div class="relative p-4 border-b border-white/10 bg-gradient-to-r from-black/60 to-black/40 backdrop-blur-xl">
            <div class="flex justify-between items-center">
              <div class="flex items-center space-x-3">
                <h3 class="text-white text-xl font-bold">{{ currentProject?.titre }}</h3>
                <div class="flex items-center space-x-2">
                  <span v-for="tech in currentProject?.technologies" 
                        :key="tech"
                        class="px-2 py-1 text-xs font-medium rounded-full bg-yellow-400/10 text-yellow-400 border border-yellow-400/20">
                    {{ tech }}
                  </span>
                </div>
              </div>
              <div class="flex items-center space-x-3">
                <button 
                  @click="toggleAutoPlay"
                  class="text-white/80 hover:text-yellow-400 transition-all duration-300 transform hover:scale-110"
                >
                  <i class="fas text-lg" :class="isAutoPlaying ? 'fa-pause' : 'fa-play'"></i>
                </button>
                <button 
                  @click="toggleFullscreen"
                  class="text-white/80 hover:text-yellow-400 transition-all duration-300 transform hover:scale-110"
                >
                  <i class="fas text-lg" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
                </button>
                <button 
                  @click="closeGallery" 
                  class="text-white/80 hover:text-yellow-400 transition-all duration-300 transform hover:rotate-90"
                >
                  <i class="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Main content area -->
          <div class="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
            <div class="relative w-full max-w-5xl">
              <!-- Navigation buttons -->
              <button 
                v-if="currentImageIndex > 0"
                @click="prevImage"
                class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-black/70 text-white p-3 rounded-full hover:bg-black/90 hover:scale-110 transition-all duration-300 z-10 shadow-xl backdrop-blur-sm"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              
              <!-- Image container -->
              <div 
                class="relative overflow-hidden rounded-3xl shadow-2xl transform transition-all duration-500 gallery-modal-container group"
                ref="imageContainer"
              >
                <!-- Subtle ambient glow -->
                <div class="absolute inset-0 bg-gradient-radial from-yellow-400/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-all duration-700 transform scale-125"></div>
                
                <!-- Simple elegant frame -->
                <div class="absolute inset-2 border border-white/10 rounded-2xl opacity-0 group-hover:opacity-80 transition-all duration-500"></div>
                
                <!-- Clean background -->
                <div class="absolute inset-0 bg-black/50 backdrop-filter backdrop-blur-sm"></div>
                
                <!-- Main image - fixed, no zoom/pan functionality -->
                <div class="relative z-10 h-full w-full flex items-center justify-center py-8">
                  <img 
                    v-if="currentGallery && currentGallery[currentImageIndex]"
                    :src="currentGallery[currentImageIndex]" 
                    :alt="`Gallery image ${currentImageIndex + 1}`" 
                    class="gallery-modal-image object-contain max-h-[65vh] transition-all duration-500 ease-out shadow-[0_0_40px_rgba(255,215,0,0.1)]"
                    @error="handleImageError"
                    ref="galleryImage"
                  />
                  
                  <!-- Simple reflection effect -->
                  <div class="absolute bottom-0 left-0 right-0 h-16 overflow-hidden opacity-30 blur-[1px] pointer-events-none">
                    <img 
                      v-if="currentGallery && currentGallery[currentImageIndex]"
                      :src="currentGallery[currentImageIndex]" 
                      class="w-full h-full object-contain scale-y-[-1] transform translate-y-0.5 opacity-70"
                    />
                    <div class="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-transparent"></div>
                  </div>
                </div>
                
                <!-- Minimal image counter -->
                <div class="absolute bottom-4 right-4 bg-black/40 backdrop-blur-md px-3 py-1 rounded-lg border border-white/10 text-white/90 text-sm font-medium">
                  {{ currentImageIndex + 1 }}/{{ currentGallery?.length }}
                </div>
                
                <!-- Loading state with simple animation -->
                <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md z-20">
                  <div class="w-12 h-12 border-3 border-yellow-400/20 border-t-yellow-400 rounded-full animate-spin"></div>
                </div>
              </div>

              <button 
                v-if="currentImageIndex < currentGallery.length - 1"
                @click="nextImage"
                class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-black/70 text-white p-3 rounded-full hover:bg-black/90 hover:scale-110 transition-all duration-300 z-10 shadow-xl backdrop-blur-sm"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
              
              <!-- Bouton de fermeture flottant -->
              <button 
                @click="closeGallery"
                class="absolute top-20 right-4 bg-black/70 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-yellow-500 transition-all duration-300 z-20 shadow-xl backdrop-blur-sm border border-white/10 animate-pulse-subtle"
                title="Fermer la galerie"
              >
                <i class="fas fa-times text-xl"></i>
              </button>
            </div>
          </div>

          <!-- Thumbnails section -->
          <div class="p-4 bg-gradient-to-t from-black/95 to-transparent backdrop-blur-xl">
            <div class="flex flex-col items-center space-y-4">
              <!-- Progress bar -->
              <div class="w-full max-w-2xl">
                <div class="flex justify-between items-center mb-2">
                  <span class="text-white/90 text-xs font-medium">Image {{ currentImageIndex + 1 }} sur {{ currentGallery?.length }}</span>
                  <span class="text-yellow-400 text-xs font-medium">{{ currentProject?.categorie }}</span>
                </div>
                <div class="h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    class="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all duration-300"
                    :style="{ width: `${((currentImageIndex + 1) / currentGallery?.length) * 100}%` }"
                  ></div>
                </div>
              </div>

              <!-- Thumbnails -->
              <div class="flex justify-center gap-3 overflow-x-auto w-full max-w-4xl px-4">
                <div 
                  v-for="(img, index) in currentGallery" 
                  :key="index"
                  @click="currentImageIndex = index"
                  class="flex-shrink-0 cursor-pointer group relative"
                >
                  <div 
                    class="relative rounded-lg overflow-hidden border-2 transition-all duration-300 transform"
                    :class="currentImageIndex === index ? 'border-yellow-400 scale-105' : 'border-transparent hover:border-yellow-400/50'"
                  >
                    <img 
                      :src="img" 
                      :alt="`Thumbnail ${index + 1}`"
                      class="w-20 h-20 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div class="absolute bottom-1 left-1 right-1 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Image {{ index + 1 }}
                    </div>
                    <!-- Active indicator -->
                    <div 
                      v-if="currentImageIndex === index"
                      class="absolute top-1 right-1 w-2 h-2 rounded-full bg-yellow-400 animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
export default {
  name: "RealisationsSection",
  data() {
    return {
      categorieActive: "Tous",
      categories: ["Tous", "Site Vitrine", "e-commerce", "Portfolio", "Application Mobile"],
      projets: [
        // {
        //   id: 1,
        //   titre: "Nou Ka Vann",
        //   categorie: "e-commerce",
        //   description:
        //     "Nou Ka Vann est un site web de vente de plantes médicinales originaires des DOM-TOM. Entièrement réalisé en HTML et CSS. C'est mon premier site internet.",
        //   image: require("../assets/img/NKVBlack.png"), // Remplacer par une image existante
        //   technologies: ["HTML", "CSS", "JavaScript"],
        //   demo: "https://jade-chebakia-5b5dbd.netlify.app/",
        //   code: null,
        //   showFullDescription: false,
        // },
        // {
        //   id: 2,
        //   titre: "LuxBraces",
        //   categorie: "e-commerce",
        //   description:
        //     "Luxbraces est une boutique en ligne spécialisée dans la vente de bracelets en cuir pour hommes, réalisée avec Shopify. Ce site e-commerce offre une expérience utilisateur moderne et intuitive, permettant aux clients de découvrir et d'acheter facilement des bracelets en cuir de qualité.",
        //   image: require("../assets/img/Fichier 1.svg"), // Remplacer par une image existante
        //   technologies: ["Shopify"],
        //   demo: "https://luxbraces.com",
        //   code: null,
        //   showFullDescription: false,
        // },
        {
          id: 3,
          titre: "Cabbie",
          categorie: "Application Mobile",
          description:
            "Cabbie est une application que je développe actuellement avec mon associé pour répondre à un besoin : dans les DOM-TOM, il est souvent compliqué de trouver un taxi rapidement. L'application, en cours de développement avec React Native, permettra aux voyageurs de trouver un taxi rapidement et facilement. Par ailleurs, nous avons déjà conçu un site web en Vue.js pour accompagner l'application. Cependant, ce n'est pas notre conception finale, et des modifications seront encore apportées.",
          image: require("../assets/img/logo_cabbie.png"), // Utiliser l'image existante
          technologies: ["Vue.js"],
          demo: "https://moncabbie.fr",
          code: null,
          showFullDescription: false,
        },
        {
          id: 4,
          titre: "Upgrade Computer",
          categorie: "e-commerce",
          description:
            "Lors de mon stage au sein de l'entreprise Upgrade Computer, j'ai participé à la conception du site internet de l'entreprise à l'aide du CMS WordPress. J'ai principalement été chargé d'ajouter l'ensemble des produits à la boutique.",
          image: require("../assets/img/UPCLogo.png"),
          technologies: ["WordPress", "WooCommerce"],
          demo: "https://upgradecomputer.fr",
          code: null,
          showFullDescription: false,
        },
        {
          id: 5,
          titre: "Portfolio",
          categorie: "Portfolio",
          description:
            "Mon portfolio est un projet personnel que j'ai réalisé pour présenter mes compétences et mes projets. J'ai conçu ce site web avec Vue.js et Tailwind CSS. J'ai également utilisé des animations et des transitions pour rendre le site plus interactif et attrayant.",
          image: require("../assets/img/LogoHRWBlanc.png"),
          technologies: ["Vue.js", "Tailwind CSS"],
          demo: "https://harrow.fr",
          code: "",
          showFullDescription: false,
        },
        {
          id: 6,
          titre: "Boutique Calypso",
          categorie: "e-commerce",
          description:
            "Dans le cadre de mon devoir en communication, j'ai créé un site internet pour la boutique Calypso. Spécialisée dans la vente de bikinis, de vêtements de resort et d'articles de décoration intérieure en provenance de Bali et du Maroc. Pour ce projet, je me suis appuyé sur le contenu et l'esthétique de leur page Instagram (@calypso.mq) afin de développer une plateforme web conviviale qui reflète l'identité visuelle de la boutique. Le site présente les produits disponibles, fournit des informations pratiques telles que les horaires d'ouverture et l'adresse, et intègre des fonctionnalités pour améliorer l'expérience utilisateur.",
          image: require("../assets/logo.png"),
          technologies: ["Vue.js", "Node.js", "Tailwind CSS"],
          demo: "https://v1calypso.netlify.app",
          code: null,
          showFullDescription: false,
        },
        {
          id: 7,
          titre: "Application Lendoo",
          categorie: "Application Mobile",
          description:
            "Lendoo est une application innovante que j'ai développée lors de ma SAE 'Développement d'application complexe.' Elle révolutionne le prêt d'objets entre particuliers en permettant aux utilisateurs de suivre facilement ce qu'ils prêtent et à qui. Grâce à une interface intuitive, Lendoo offre la possibilité d'enregistrer les prêts, de définir des dates de retour et d'envoyer des rappels automatiques. Ce projet répond à un besoin quotidien tout en s'inscrivant dans une démarche d'économie collaborative et de consommation responsable, encourageant le partage plutôt que l'achat systématique d'objets peu utilisés.",
          image: require("../assets/img/pnglendoo/Fichier 18.png"),
          technologies: ["Vue.js", "Node.js", "Tailwind CSS", "React Native"],
          demo: "https://lendoo.netlify.app/",
          code: null,
          gallery: [
            "/img/MockupLendoo/7.png",
            "/img/MockupLendoo/1.png",
            "/img/MockupLendoo/2.png",
            "/img/MockupLendoo/3.png",
            "/img/MockupLendoo/4.png",
            "/img/MockupLendoo/6.png",
            "/img/MockupLendoo/5.png"
            
          ],
          showFullDescription: false,
        },
        {
          id: 8,
          titre: "Association Doucine",
          categorie: "Site Vitrine",
          description:
            "Site internet développé pour l'association DOUCINE, visant à mettre en avant les actions de l'association pour renforcer le lien social et le bien-être des jeunes retraités, des seniors et des familles. Cette plateforme intuitive, accessible et moderne permet aux membres et au public de s'informer sur les événements, services et initiatives de DOUCINE. Le site offre une expérience utilisateur optimale avec une navigation claire et des informations facilement accessibles.",
          image: require("../assets/LogoDoucine.png"),
          technologies: ["Vue.js", "Node.js", "Tailwind CSS"],
          demo: "https://association-doucine.fr",
          code: null,
          showFullDescription: false,
        },
        {
          id: 9,
          titre: "Site IUT de la Martinique",
          categorie: "Site Vitrine",
          description:
            "Conception et développement complet du site internet officiel de l'IUT de la Martinique. Ce projet a été réalisé en binôme dans le cadre d'une mission confiée par l'IUT aux étudiants de BUT Informatique. L'institution ne possédait pas de site internet auparavant, et nous avons été chargés de créer entièrement cette plateforme institutionnelle. Le site présente les formations, les départements, les actualités et toutes les informations essentielles pour les étudiants et le public.",
          image: require("../assets/img/IUTMartiniquelogo(1).svg"),
          technologies: ["ReactJS", "Tailwind CSS"],
          demo: "https://iut-martinique.univ-antilles.fr",
          code: null,
          showFullDescription: false,
        },
      ],
      showGallery: false,
      currentGallery: null,
      currentImageIndex: 0,
      currentProject: null,
      isLoading: false,
      isFullscreen: false,
      isAutoPlaying: false,
      autoPlayInterval: null
    };
  },
  computed: {
    projetsFiltres() {
      if (this.categorieActive === "Tous") {
        return this.projets;
      }
      return this.projets.filter(
        (projet) => projet.categorie === this.categorieActive
      );
    },
  },
  methods: {
    filtrerPar(categorie) {
      this.categorieActive = categorie;
    },
    toggleDescription(projet) {
      projet.showFullDescription = !projet.showFullDescription;
    },
    openGallery(projet) {
      this.currentProject = projet;
      this.currentGallery = projet.gallery;
      this.currentImageIndex = 0;
      this.showGallery = true;
      document.body.style.overflow = 'hidden';
      
      // Ajout d'un écouteur d'événement pour la touche Escape
      document.addEventListener('keydown', this.handleKeyDown);
    },
    closeGallery() {
      this.showGallery = false;
      this.currentGallery = null;
      this.currentImageIndex = 0;
      this.currentProject = null;
      document.body.style.overflow = '';
      
      // S'assurer que l'auto-play est arrêté
      if (this.isAutoPlaying) {
        this.toggleAutoPlay();
      }
      
      // Suppression de l'écouteur d'événement pour la touche Escape
      document.removeEventListener('keydown', this.handleKeyDown);
    },
    handleKeyDown(e) {
      // Fermer avec la touche Escape
      if (e.key === 'Escape' && this.showGallery) {
        this.closeGallery();
      }
      
      // Navigation avec les flèches gauche/droite
      if (e.key === 'ArrowLeft' && this.currentImageIndex > 0) {
        this.prevImage();
      }
      
      if (e.key === 'ArrowRight' && this.currentImageIndex < this.currentGallery.length - 1) {
        this.nextImage();
      }
    },
    nextImage() {
      if (this.currentImageIndex < this.currentGallery.length - 1) {
        this.currentImageIndex++;
      }
    },
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        this.isFullscreen = true;
      } else {
        document.exitFullscreen();
        this.isFullscreen = false;
      }
    },
    toggleAutoPlay() {
      this.isAutoPlaying = !this.isAutoPlaying;
      if (this.isAutoPlaying) {
        this.autoPlayInterval = setInterval(() => {
          if (this.currentImageIndex < this.currentGallery.length - 1) {
            this.currentImageIndex++;
          } else {
            this.currentImageIndex = 0;
          }
        }, 3000);
      } else {
        clearInterval(this.autoPlayInterval);
      }
    },
    handleImageError(e) {
      console.error('Error loading image:', e);
      this.isLoading = false;
    }
  },
  mounted() {
    // Aucun listener d'événement nécessaire
  },
  beforeUnmount() {
    // Nettoyer l'intervalle d'auto-play si actif
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
    }
  },
};
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(to right, #ffd700, #ffa500);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  display: inline-block;
}

/* Custom radial gradient utility (used in image gallery) */
.bg-gradient-radial {
  background-image: radial-gradient(var(--tw-gradient-stops));
}

/* Filtres */
.filter-btn {
  font-weight: 500;
  border: 1px solid transparent;
  transform: translateY(0);
  box-shadow: 0 0 0 rgba(255, 215, 0, 0);
}

.active-filter {
  background: linear-gradient(to right, #ffd700, #ffa500);
  color: #1a1a1a;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(255, 215, 0, 0.4);
}

.inactive-filter {
  background: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.inactive-filter:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-1px);
}

/* Grille Masonry */
.masonry-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 1.5rem;
}

@media (min-width: 768px) {
  .masonry-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .masonry-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Cartes de projet */
.projet-card {
  position: relative;
  background: linear-gradient(145deg, #3a3d44, #4a4d54);
  border-radius: 16px;
  transition: all 0.4s ease;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.projet-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.5);
  border-color: rgba(255, 215, 0, 0.2);
}

/* Ruban de catégorie */
.category-ribbon {
  position: absolute;
  top: 20px;
  right: -30px;
  background: linear-gradient(to right, #ffd700, #ffa500);
  color: #1a1a1a;
  padding: 5px 30px;
  font-size: 0.75rem;
  font-weight: 600;
  transform: rotate(45deg);
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

/* Image et overlay */
.projet-image-container {
  position: relative;
  overflow: hidden;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  background-color: #2a2a2a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.projet-img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  transition: transform 0.5s ease;
}

.projet-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.projet-card:hover .projet-img {
  transform: scale(1.05);
}

.projet-card:hover .projet-overlay {
  opacity: 1;
}

/* Actions d'overlay */
.overlay-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  transform: translateY(20px);
  transition: transform 0.3s ease;
}

.projet-card:hover .overlay-actions {
  transform: translateY(0);
}

.action-btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.demo-btn {
  background: linear-gradient(to right, #ffd700, #ffa500);
  color: #1a1a1a;
}

.demo-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(255, 215, 0, 0.4);
}

.code-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.code-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Contenu du projet */
.projet-content {
  padding: 1.5rem;
}

.projet-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.75rem;
  background: linear-gradient(to right, #fff, #e2e2e2);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.projet-description {
  color: #d1d5db;
  font-size: 0.95rem;
  line-height: 1.6;
}

.read-more-btn {
  margin-top: 0.5rem;
  color: #ffd700;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.3s ease;
}

.read-more-btn:hover {
  color: #ffa500;
}

/* Badges de technologies */
.tech-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.tech-badge {
  font-size: 0.7rem;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Animations de transition */
.projet-transition-enter-active,
.projet-transition-leave-active {
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.projet-transition-enter-from,
.projet-transition-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.projet-transition-move {
  transition: transform 0.8s ease;
}

/* Modal animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Custom scrollbar for thumbnails */
::-webkit-scrollbar {
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(to right, #ffd700, #ffa500);
  border-radius: 4px;
  border: 2px solid rgba(0, 0, 0, 0.2);
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to right, #ffa500, #ffd700);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .projet-image-container {
    height: 100px;
  }
  
  .projet-img {
    max-height: 60%;
    max-width: 60%;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .projet-image-container {
    height: 120px;
  }
  
  .projet-img {
    max-height: 65%;
    max-width: 65%;
  }
}

@media (min-width: 1025px) {
  .projet-image-container {
    height: 150px;
  }
  
  .projet-img {
    max-height: 70%;
    max-width: 70%;
  }
}

/* Gallery modal specific styles */
.gallery-modal-container {
  width: 100%;
  height: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(18, 18, 18, 0.9);
  max-width: 90vw;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  isolation: isolate;
}

.gallery-modal-image {
  max-height: 70vh;
  max-width: 90%;
  transition: all 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
  filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.1));
  transform-origin: center;
}

.gallery-modal-image.scale-150 {
  max-height: none;
  max-width: none;
}

/* Main content with enhanced navigation and zoom */
.flex-1.flex.items-center.justify-center.p-8.relative.overflow-hidden {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.relative.w-full.max-w-6xl {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
}

/* Thumbnails specific styles */
.flex.justify-center.gap-3.overflow-x-auto.w-full.max-w-4xl.px-4 {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 215, 0, 0.5) rgba(0, 0, 0, 0.1);
  padding-bottom: 0.5rem;
}

.flex.justify-center.gap-3.overflow-x-auto.w-full.max-w-4xl.px-4::-webkit-scrollbar {
  height: 6px;
}

.flex.justify-center.gap-3.overflow-x-auto.w-full.max-w-4xl.px-4::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.flex.justify-center.gap-3.overflow-x-auto.w-full.max-w-4xl.px-4::-webkit-scrollbar-thumb {
  background: linear-gradient(to right, rgba(255, 215, 0, 0.5), rgba(255, 165, 0, 0.5));
  border-radius: 3px;
}

.flex.justify-center.gap-3.overflow-x-auto.w-full.max-w-4xl.px-4::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to right, rgba(255, 215, 0, 0.7), rgba(255, 165, 0, 0.7));
}

/* Thumbnail container hover effects */
.flex-shrink-0.cursor-pointer.group.relative {
  transition: transform 0.3s ease;
}

.flex-shrink-0.cursor-pointer.group.relative:hover {
  transform: translateY(-3px);
}

/* Active thumbnail styles */
.border-yellow-400.scale-105 {
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
}

/* Thumbnail image hover effect */
.w-20.h-20.object-cover.transition-transform.duration-300.group-hover\:scale-110 {
  filter: brightness(0.9);
  transition: filter 0.3s ease;
}

.w-20.h-20.object-cover.transition-transform.duration-300.group-hover\:scale-110:hover {
  filter: brightness(1);
}

/* Active indicator animation */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-pulse {
  animation: pulse 2s infinite;
}

/* Animation de pulsation subtile pour le bouton de fermeture */
@keyframes pulse-subtle {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 215, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 215, 0, 0);
  }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s infinite;
}
</style>
